import React from 'react';
import { FileText, Image, Mail, Phone, CalendarClock, AlertTriangle } from 'lucide-react';

const FREE_PAPER_GUIDELINES = [
  'Any AGOS member can submit a PAPER.',
  'A free paper can have one presenting author and up to 3 co-authors (4 authors in total).',
  'One of the above 4 authors can be the presenting author of the free paper.',
  'Abstracts are to be submitted online only. No abstracts shall be accepted as email, Fax, Hard copies etc.',
  'All abstracts undergo an evaluation process.',
  'Based on the evaluation, a member can present a MAXIMUM of TWO FREE PAPERS.',
  'The presenting author and the co-authors SHOULD be members of AGOS. Each author must have contributed to the scientific part of the free paper being submitted.',
  'Ophthalmic authors who are non-members of AGOS should become a member of AGOS to be a presenting author / co-author of the free paper.',
  'Non-ophthalmic co-authors can be included (if relevant) & they can be non-members.',
  'Free paper awards will be given to all presenting authors and certificates will be given to all co-authors.',
  'Eligibility for AGOS Award – The submitted paper should not have been published in an indexed journal / submitted for publication in an indexed journal. Paper presented in other conferences can be submitted and shall be eligible for the AGOS award. If any ambiguity is found anytime, this would amount to plagiarism and the presenter and all co-authors will be debarred from submitting to AGOC for the next 3 years.',
  'Free Paper duration is 6 minutes only.',
  'No change in presenting author is allowed during the Free Paper final session.',
  'Institution / hospital affiliation and doctor\u2019s name in the title or abstract should NOT be mentioned and will lead to automatic rejection.',
  'A single case report will not be considered for a Free Paper.',
];

const EPOSTER_GUIDELINES = [
  'Introduction',
  'Material and Methods',
  'Discussion',
  'Conclusion',
  'Use Times New Roman or Arial fonts.',
  'Use the first slide to show the title of the E-Poster and the authors\u2019 information.',
  'Font should be no smaller than 18 points.',
  'Maximum of 120 words on each slide.',
  'Use a plain background without patterns or dark colours.',
  'Use clear headings and leave spaces between sections.',
  'Pictures and graphics should be clearly labelled and described.',
  'Pictures and graphics should be of good quality.',
  'Text should not be obscured by images.',
  'Remember that simpler posters are always more attractive and easier to read.',
  'Do not fit too much information on one slide.',
  'Correct spelling and punctuation make your poster more attractive and informative.',
  'E-Posters should contain no video clips / animation files.',
  'The file should be not more than 5 MB (larger files will be rejected automatically).',
];

const AbstractGuidelines = ({ showSubmission = true }) => {
  return (
    <div className="space-y-8" data-testid="abstract-guidelines">
      {/* Deadline banner */}
      <div className="glass-card p-5 border-2 border-yellow-500/40 bg-yellow-500/5 flex items-center gap-3 animate-pulse" data-testid="abstract-deadline-banner">
        <CalendarClock className="h-6 w-6 text-yellow-300 flex-shrink-0" />
        <p className="text-yellow-300 font-bold text-lg">
          Last date to submit the final Paper &amp; Poster (for selected abstracts): 7th October 2026
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Free Paper */}
        <div className="glass-card p-6 md:p-8 border border-[#FF6F4A]/25" data-testid="free-paper-guidelines">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-full bg-[#FF6F4A]/20 flex items-center justify-center flex-shrink-0">
              <FileText className="h-5 w-5 text-[#FF6F4A]" />
            </div>
            <h3 className="text-xl font-bold text-white">Free Paper Guidelines</h3>
          </div>
          <ol className="space-y-3">
            {FREE_PAPER_GUIDELINES.map((g, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                <span className="text-[#FF6F4A] font-bold flex-shrink-0 min-w-[20px]">{i + 1}.</span>
                <span>{g}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* E-Poster */}
        <div className="glass-card p-6 md:p-8 border border-purple-500/25" data-testid="eposter-guidelines">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
              <Image className="h-5 w-5 text-purple-300" />
            </div>
            <h3 className="text-xl font-bold text-white">E-Poster Guidelines</h3>
          </div>
          <p className="text-sm text-slate-300 mb-4 leading-relaxed">
            E-Posters should consist of a <span className="text-white font-semibold">maximum of 12 Microsoft PowerPoint slides</span> (including the title slide) and should offer information on the following:
          </p>
          <ol className="space-y-3">
            {EPOSTER_GUIDELINES.map((g, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                <span className="text-purple-300 font-bold flex-shrink-0 min-w-[20px]">{i + 1}.</span>
                <span>{g}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {showSubmission && (
        <div className="glass-card p-6 md:p-8 border border-[#FF6F4A]/25" data-testid="abstract-submission-info">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-5 w-5 text-[#FF6F4A]" />
            <h3 className="text-lg font-bold text-white">How to Submit Your Paper / Poster</h3>
          </div>
          <p className="text-sm text-slate-300 mb-4 leading-relaxed">
            Submit your final Paper / Poster to{' '}
            <a href="mailto:paper.agoc2026@yahoo.com" className="text-[#FF6F4A] font-semibold hover:underline">paper.agoc2026@yahoo.com</a>{' '}
            with a CC to{' '}
            <a href="mailto:Shobitanair_2@yahoo.co.in" className="text-[#FF6F4A] font-semibold hover:underline">Shobitanair_2@yahoo.co.in</a>{' '}
            and{' '}
            <a href="mailto:sgaosurat2025@gmail.com" className="text-[#FF6F4A] font-semibold hover:underline">sgaosurat2025@gmail.com</a>.
          </p>
          <div className="border-t border-white/10 pt-4">
            <p className="text-sm text-slate-400 mb-2 font-semibold">For any queries regarding Paper &amp; Poster:</p>
            <div className="flex flex-col sm:flex-row gap-3 text-sm text-slate-300">
              <span className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#FF6F4A]" /> Dr. Shobita Shah — 9585817219</span>
              <span className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#FF6F4A]" /> <a href="mailto:Shobitanair_2@yahoo.co.in" className="hover:underline">Shobitanair_2@yahoo.co.in</a></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbstractGuidelines;
